'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { notFound, useParams, useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import qs from 'qs';
import { ArrowLeft, ArrowRight, Calendar, Clock, MapPin, Share2, DollarSign, Accessibility, X, ExternalLink, Building2, Pencil, Trash2, ImagePlus, Newspaper } from 'lucide-react';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useAuthModal } from '@/context/AuthModalContext';
import {
  Event,
  EventType,
  RecurringEventScope,
  getEventCategories,
  getEventCategorySlugs,
  isRecurringEventType,
} from '@/types';
import {
  Modal,
  Toast,
  ReportButton,
  TimePicker,
  CreateNewsModal,
  CrisisModal,
  InappropriateLanguageModal,
  InappropriateLanguageBanner,
  ERROR_BORDER_CLASS,
} from '@/components/ui';
import { useContentModeration } from '@/hooks/useContentModeration';
import { useUserStore } from '@/store/userStore';
import { useGroupStore } from '@/store/groupStore';
import { useEventStore } from '@/store/eventStore';
import { AttendeeAvatars, EventAttendeesTab, EventForumTab } from '@/components/events';
import { ImageCarousel } from '@/components/news';
import { useLanguage } from '@/context/LanguageContext';
import { ActivityLogger } from '@/lib/activityLog';
import { fetchStrapiCollection } from '@/lib/strapi';
import {
  getImageUploadCopy,
  prepareImageUploads,
  MAX_MULTI_IMAGE_UPLOAD_COUNT,
} from '@/lib/image-upload';
import { getStrapiImageUrl } from '@/lib/image-utils';
import { linkifyText } from '@/lib/linkify';
import { getEventCategoryLabelI18n, sortByLabel } from '@/lib/taxonomy-labels';
import {
  canUseMessaging,
  formatTime12H,
  getEventCostLabel,
  legacyEventCostLabel,
  getUserProfileHref,
  normalizeExternalUrl,
  parseDateOnly,
  startOfToday,
} from '@/utils';
import { FORM_LIMITS } from '@/lib/formLimits';

const baseUrl = '/api/strapi';
type EventSocialTab = 'forum' | 'attendees';
const MAX_CATEGORIES = 4;
// Stable id so the "cost is required" reason can be associated with both the
// cost input and the Save button via aria-describedby.
const EDIT_COST_REQUIRED_ID = 'event-detail-edit-cost-required';
const EDIT_END_DATE_ERROR_ID = 'event-detail-edit-end-date-error';

export default function EventDetailPage() {
  const { t, language } = useLanguage();
  const params = useParams();
  const router = useRouter();
  const documentId = params.documentId as string;
  const { currentUser } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { users, fetchUsers } = useUserStore();
  const { isUserMemberOfGroup, fetchMyMemberships, getUserRole } = useGroupStore();
  const { updateEvent, deleteEvent, categories, fetchCategories } = useEventStore();
  const uploadCopy = getImageUploadCopy(language, 'multiple');

  const [event, setEvent] = useState<Event | null>(null);
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  // Sibling occurrence rows of the loaded event's series (same seriesId).
  const [seriesOccurrences, setSeriesOccurrences] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const [activeSocialTab, setActiveSocialTab] = useState<EventSocialTab>('forum');

  const [showPostModal, setShowPostModal] = useState(false);
  const [postModalFiles, setPostModalFiles] = useState<{ file: File; preview: string }[]>([]);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRecurringDeleteScopeModal, setShowRecurringDeleteScopeModal] = useState(false);
  const [selectedRecurringDeleteScope, setSelectedRecurringDeleteScope] = useState<RecurringEventScope | null>(null);
  const [showRecurringEditScopeModal, setShowRecurringEditScopeModal] = useState(false);
  const [selectedRecurringEditScope, setSelectedRecurringEditScope] = useState<RecurringEventScope | null>(null);
  // Multi-image edit state (mirrors the Groups edit pattern): the existing media
  // the admin kept, plus newly picked files and their object-URL previews.
  const MAX_EDIT_IMAGES = MAX_MULTI_IMAGE_UPLOAD_COUNT;
  const [editExistingImages, setEditExistingImages] = useState<{ id: number; url: string; alternativeText?: string }[]>([]);
  const [editNewImages, setEditNewImages] = useState<File[]>([]);
  const [editNewPreviews, setEditNewPreviews] = useState<string[]>([]);
  const [editImagesError, setEditImagesError] = useState<string | null>(null);
  // Only send the image set to the backend when the admin actually changed it.
  // A text-only save then leaves the `images`/`image` relations untouched rather
  // than rewriting them — so an existing photo can never be cleared by a save
  // that didn't touch images (MWBI-889 review).
  const [didEditImages, setDidEditImages] = useState(false);
  // Guards the async add against re-entrancy: the slot count is read before the
  // compression await, so without this two overlapping adds could both pass the
  // cap check and append past MAX_EDIT_IMAGES (MWBI-889 review).
  const isAddingImagesRef = useRef(false);
  const [isAddingImages, setIsAddingImages] = useState(false);
  const editImagesInputRef = useRef<HTMLInputElement>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    date: '',
    // Only meaningful while a recurring cadence is selected: it is the last day
    // the series may fall on, and it is what turns a standalone event into a
    // real series on save (MWBI-955).
    endDate: '',
    timeStart: '',
    timeEnd: '',
    location: '',
    cost: '',
    bookingLink: '',
    openToPublic: true,
    eventType: 'one-off' as EventType,
    categorySlugs: [] as string[],
  });

  const isAttending = Boolean(currentUser && event?.attendees?.some(a => a.id === currentUser.id));
  const attendeeCount = event?.attendeeCount ?? 0;
  const normalizedBookingLink = normalizeExternalUrl(event?.bookingLink);

  // Permission check: group admin/editor or MWBI admin
  const isAdmin = currentUser?.role?.type === 'admin' || currentUser?.role?.type === 'superadmin';
  const userGroupRole = currentUser && event?.group?.id ? getUserRole(currentUser.id, event.group.id) : null;
  const isGroupAdminOrEditor = userGroupRole === 'admin' || userGroupRole === 'editor';
  const isAuthor = Boolean(currentUser && event?.author?.id === currentUser.id);
  const canEdit = isAuthor || isAdmin || isGroupAdminOrEditor;
  const canViewEventTabs = canEdit || isAttending;
  // After the MWBI-684 model change each occurrence is its own row. The scope
  // modal (this-event vs this-and-future) is only relevant when the row
  // belongs to a recurring series (has a seriesId).
  const isSeriesOccurrence = Boolean(event?.seriesId);
  const shouldPromptForRecurringScope = isSeriesOccurrence;

  // Compare the occurrence's own date to today; a past occurrence can't be RSVP'd.
  const isPastEvent = Boolean(event && parseDateOnly(event.date) < startOfToday());

  // Resolve previous/next sibling occurrences from the fetched series rows
  // (rows sharing this event's seriesId), ordered by date.
  const currentSeriesIndex = event
    ? seriesOccurrences.findIndex((occurrence) => occurrence.documentId === event.documentId)
    : -1;
  const previousOccurrence = currentSeriesIndex > 0
    ? seriesOccurrences[currentSeriesIndex - 1]
    : null;
  const nextOccurrence = currentSeriesIndex >= 0 && currentSeriesIndex < seriesOccurrences.length - 1
    ? seriesOccurrences[currentSeriesIndex + 1]
    : null;
  // An occurrence row keeps its cadence in eventType but has a null endDate.
  // Derive the series end from the latest sibling row's date.
  const seriesEndDate = seriesOccurrences.length > 0
    ? seriesOccurrences[seriesOccurrences.length - 1].date
    : null;

  // Content moderation over the edit form's human-text fields (MWBI-870),
  // reusing the shared MWBI-866 hook. Scan title + description + location only
  // (joined with `\n` so the `\b...\b` inappropriate regex and the crisis
  // substring check never match across fields); bookingLink (a URL) is excluded.
  // Like CreateEventModal — whose parents render it unconditionally, so it stays
  // mounted and merely returns null while closed — this detail page stays mounted
  // across the read/edit toggle, so the scanned text is gated on `isEditMode` to
  // keep detection side-effects (auto-opening a modal) from firing outside the
  // edit form. Called above the loading / notFound early-returns below to
  // preserve the Rules of Hooks.
  const moderationText = isEditMode
    ? [editForm.title, editForm.description, editForm.location, editForm.cost].join('\n')
    : '';
  const {
    hasInappropriate,
    showCrisisModal,
    showInappropriateModal,
    dismissCrisisModal,
    dismissInappropriateModal,
  } = useContentModeration(moderationText);

  // Close any open moderation modal when leaving edit mode. The detail page
  // stays mounted across the read/edit toggle and only stops *rendering* the
  // modals outside edit mode — the hook's modal-open state otherwise persists.
  // Since handleStartEdit re-seeds editForm from the (clean) saved event on the
  // next session, a modal left open on exit could resurface over clean content
  // when edit mode is re-entered (reachable via keyboard nav). Dismissing here
  // keeps each edit session self-contained without changing the shared hook's
  // semantics for other callers (MWBI-870 review).
  useEffect(() => {
    if (!isEditMode) {
      dismissCrisisModal();
      dismissInappropriateModal();
    }
  }, [isEditMode, dismissCrisisModal, dismissInappropriateModal]);

  // Cost is required (MWBI-919). Derived once so the disabled Save button, the
  // scope modal's confirm button, the inline reason and both save-path guards
  // all read the same condition and cannot drift apart. Scoped to edit mode so
  // the read view is never treated as "missing" a cost.
  const isEditCostMissing = isEditMode && !editForm.cost.trim();

  // Picking a recurring cadence in the edit form converts a standalone event
  // into a real series, which requires an end date (MWBI-955) -- exactly as
  // CreateEventModal requires one at creation. Derived here for the same reason
  // as isEditCostMissing: the input, the inline reason, the Save button and
  // every save-path guard must read one condition.
  // MUST mirror the end-date input's render condition exactly. A series
  // occurrence also carries a recurring eventType, but it never converts
  // anything (its edits route through update-occurrences, which strips both
  // endDate and eventType) and therefore renders no end-date input. Requiring
  // one here would disable Save with a reason the user has no field to satisfy.
  const isEditRecurring =
    isEditMode && !isSeriesOccurrence && isRecurringEventType(editForm.eventType);
  const isEditEndDateMissing = isEditRecurring && !editForm.endDate;
  // A single-day series is legitimate (start === end yields one occurrence), so
  // only a genuinely earlier end date is invalid.
  const isEditEndDateBeforeStart =
    isEditRecurring &&
    Boolean(editForm.endDate) &&
    Boolean(editForm.date) &&
    parseDateOnly(editForm.endDate) < parseDateOnly(editForm.date);
  const isEditEndDateInvalid = isEditEndDateMissing || isEditEndDateBeforeStart;
  const isEditBlocked = hasInappropriate || isEditCostMissing || isEditEndDateInvalid;
  // Every blocking reason must reach assistive tech, not just the first one.
  const editBlockedDescribedBy =
    [
      isEditCostMissing ? EDIT_COST_REQUIRED_ID : null,
      isEditEndDateInvalid ? EDIT_END_DATE_ERROR_ID : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  const fetchEventDetail = useCallback(async (
    options?: {
      preserveCurrentEventOnError?: boolean;
      logErrors?: boolean;
    },
  ) => {
    const {
      preserveCurrentEventOnError = false,
      logErrors = true,
    } = options ?? {};
    setLoading(true);
    try {
      const groupMembersPromise = currentUser
        ? fetchMyMemberships(currentUser.id)
        : Promise.resolve();
      const query = qs.stringify({
        populate: {
          image: true,
          images: true,
          categories: true,
          group: { fields: ['id', 'documentId', 'name'] },
          attendees: {
            fields: ['id'],
          },
          author: {
            fields: ['id'],
          },
        },
      }, { encodeValuesOnly: true });
      const res = await fetch(`${baseUrl}/events/${documentId}?${query}`, {
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('Event not found');
      const json = await res.json();
      const fetchedEvent = json.data as Event;

      await groupMembersPromise;

      if (fetchedEvent.openToPublic === false && fetchedEvent.group?.id && fetchedEvent.group.documentId) {
        const canViewPrivateEvent = currentUser
          ? isUserMemberOfGroup(currentUser.id, fetchedEvent.group.id)
          : false;

        if (!canViewPrivateEvent) {
          const joinGroupParams = new URLSearchParams({
            eventTitle: fetchedEvent.title,
            groupName: fetchedEvent.group.name,
          });
          router.push(`/groups/${fetchedEvent.group.documentId}/join-to-view?${joinGroupParams.toString()}`);
          return;
        }
      }

      setEvent(fetchedEvent);

      // Fetch the sibling occurrence rows for this series (rows sharing the
      // same seriesId) so prev/next navigation and the "until X" series-end
      // text can be derived from real rows rather than virtual date math.
      // A series can hold up to MAX_OCCURRENCES_PER_SERIES (370) rows, while a
      // raw /api/events read is capped at 100 — fetchStrapiCollection pages
      // through the whole collection so a long series is not truncated.
      if (fetchedEvent.seriesId) {
        try {
          const occurrences = await fetchStrapiCollection<Event>('events', {
            filters: {
              seriesId: { $eq: fetchedEvent.seriesId },
              deletedAt: { $null: true },
            },
            fields: ['id', 'documentId', 'date', 'title', 'seriesId', 'eventType'],
            sort: ['date:asc'],
          });
          setSeriesOccurrences(occurrences);
        } catch (seriesError) {
          if (logErrors) {
            console.error('Error fetching series occurrences:', seriesError);
          }
          setSeriesOccurrences([]);
        }
      } else {
        setSeriesOccurrences([]);
      }

      // The "More events" list is supplementary — a failure here must not
      // discard the event we already loaded. Degrade to an empty list, like
      // the series-occurrences fetch above.
      try {
        const events = await fetchStrapiCollection<Event>('events', {
          filters: { deletedAt: { $null: true } },
          populate: {
            image: true,
            images: true,
            categories: true,
            group: { fields: ['id', 'documentId', 'name'] },
            attendees: { fields: ['id'] },
          },
          sort: ['date:asc'],
        });
        setAllEvents(events);
      } catch (allEventsError) {
        if (logErrors) {
          console.error('Error fetching related events:', allEventsError);
        }
        setAllEvents([]);
      }
    } catch (error) {
      if (logErrors) {
        console.error('Error fetching event:', error);
      }

      if (!preserveCurrentEventOnError) {
        setEvent(null);
      }
    }
    setLoading(false);
  }, [currentUser, documentId, fetchMyMemberships, isUserMemberOfGroup, router]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [documentId]);

  useEffect(() => {
    if (documentId) {
      fetchEventDetail();
    }
  }, [documentId, fetchEventDetail]);

  // The member directory is signed-in only: a signed-out request just gets a
  // 403, which the store logs and RUM reports as a JS error (#1473). Keyed on
  // the id so a session re-check that returns a fresh user object for the same
  // member does not reload the whole directory.
  useEffect(() => {
    if (currentUser?.id === undefined) return;
    fetchUsers();
  }, [currentUser?.id, fetchUsers]);

  const handleRSVP = async () => {
    if (!currentUser) {
      openAuthModal();
      return;
    }
    if (!event || rsvpLoading || isPastEvent) return;

    if (event.openToPublic === false && event.group?.id) {
      const isMember = isUserMemberOfGroup(currentUser.id, event.group.id);
      if (!isMember && !isAttending) {
        setToast({ message: t.eventDetail.membersOnlyRsvp, type: 'error' });
        setTimeout(() => setToast(null), 4000);
        return;
      }
    }

    setRsvpLoading(true);
    try {
      const res = await fetch(`${baseUrl}/events/${event.documentId}/toggle-attend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to update RSVP');
      const json = await res.json();
      setEvent(prev => {
        if (!prev) return prev;
        const updatedAttendees = json.data.attending
          ? [...(prev.attendees || []), { id: currentUser.id }]
          : (prev.attendees || []).filter((a: { id: number }) => a.id !== currentUser.id);
        return { ...prev, attendees: updatedAttendees, attendeeCount: json.data.attendeeCount };
      });
      setToast({ message: isAttending ? t.eventDetail.rsvpCancelled : t.eventDetail.rsvpConfirmed, type: 'success' });
    } catch (error) {
      console.error('src/app/events/[documentId]/page.tsx', error);
      setToast({ message: t.eventDetail.failedToUpdateRsvp, type: 'error' });
    }
    setRsvpLoading(false);
    setTimeout(() => setToast(null), 3000);
  };

  const convertTo24Hour = (time: string): string => {
    if (!time) return '00:00';

    const [timePart, modifier] = time.split(' ');
    const parts = timePart.split(':').map(Number);
    let hours = parts[0];
    const minutes = parts[1];

    if (modifier === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (modifier === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleAddToCalendar = (platform: 'google' | 'outlook') => {
    if (!event) return;

    // Convert times to 24-hour format
    const startTime24 = convertTo24Hour(event.timeStart || '00:00 AM');
    const endTime24 = convertTo24Hour(event.timeEnd || '11:59 PM');

    const startDate = new Date(`${event.date}T${startTime24}:00`);
    const endDate = new Date(`${event.date}T${endTime24}:00`);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      setToast({ message: t.eventDetail.invalidDateTime, type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    const title = encodeURIComponent(event.title);
    const description = encodeURIComponent(event.description || '');
    const location = encodeURIComponent(event.location);

    if (platform === 'google') {
      // Google Calendar format: YYYYMMDDTHHMMSSZ
      const formatGoogleDate = (date: Date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      };

      const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${description}&location=${location}`;
      window.open(googleUrl, '_blank');
    } else if (platform === 'outlook') {
      // Outlook format: YYYY-MM-DDTHH:MM:SS
      const formatOutlookDate = (date: Date) => {
        return date.toISOString().split('.')[0];
      };

      const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${formatOutlookDate(startDate)}&enddt=${formatOutlookDate(endDate)}&body=${description}&location=${location}`;
      window.open(outlookUrl, '_blank');
    }

    setShowCalendarOptions(false);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setToast({ message: t.groupDetail.linkCopied, type: 'success' });
    setTimeout(() => setToast(null), 3000);
  };

  const getAttendees = () => {
    if (!event?.attendees || event.attendees.length === 0) return [];
    return users.filter(user => event.attendees?.some(a => a.id === user.id));
  };

  const handleStartEdit = () => {
    if (!event) return;
    // Fetch categories when entering edit mode
    if (categories.length === 0) fetchCategories();
    // Seed the gallery with every existing photo so the admin can see and manage
    // all of them. Prefer the multi-image `images` set; fall back to the legacy
    // single `image` (only when it carries a numeric media id we can keep).
    const existingImgs = event.images && event.images.length > 0
      ? event.images.map((img) => ({ id: img.id, url: img.url, alternativeText: img.alternativeText }))
      : event.image?.id && event.image?.url
        ? [{ id: event.image.id, url: event.image.url, alternativeText: event.image.alternativeText }]
        : [];
    setEditExistingImages(existingImgs);
    setEditNewImages([]);
    setEditNewPreviews([]);
    setEditImagesError(null);
    setDidEditImages(false);
    isAddingImagesRef.current = false;
    setIsAddingImages(false);
    setSelectedRecurringDeleteScope(null);
    setSelectedRecurringEditScope(null);
    setEditForm({
      title: event.title,
      description: event.description || '',
      date: event.date,
      // Series occurrences carry a null endDate (the span has no per-occurrence
      // meaning), and so do one-offs. A standalone row left with a recurring
      // cadence by the MWBI-955 bug may still hold one -- seed it so the repair
      // path shows the organiser what was there.
      endDate: event.endDate ?? '',
      timeStart: event.timeStart || '',
      timeEnd: event.timeEnd || '',
      location: event.location,
      // Legacy rows have no `cost` yet, so seed the same text the server
      // backfill would write -- NOT the translated read-view label. `cost` is
      // stored and shown verbatim, so seeding from the localized label would
      // let a Spanish-mode editor freeze "Gratis" into a field English readers
      // also see (Qodo review, finding 2).
      cost: event.cost?.trim() || legacyEventCostLabel(event),
      bookingLink: event.bookingLink || '',
      openToPublic: event.openToPublic ?? true,
      eventType: event.eventType || 'one-off',
      categorySlugs: getEventCategorySlugs(event),
    });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    editNewPreviews.forEach((url) => URL.revokeObjectURL(url));
    setEditExistingImages([]);
    setEditNewImages([]);
    setEditNewPreviews([]);
    setEditImagesError(null);
    setDidEditImages(false);
    isAddingImagesRef.current = false;
    setIsAddingImages(false);
    setShowDeleteConfirm(false);
    setShowRecurringDeleteScopeModal(false);
    setShowRecurringEditScopeModal(false);
    setSelectedRecurringDeleteScope(null);
    setSelectedRecurringEditScope(null);
    setIsEditMode(false);
  };

  const handleEditImagesAdd = async (files: FileList | File[]) => {
    // Block a second add while one is preparing — the slot count is read before
    // the await below, so overlapping adds could otherwise oversubscribe the cap.
    if (isAddingImagesRef.current) return;
    isAddingImagesRef.current = true;
    setIsAddingImages(true);
    try {
      const allFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
      const totalCurrent = editExistingImages.length + editNewImages.length;
      const available = Math.max(0, MAX_EDIT_IMAGES - totalCurrent);
      const toAdd = allFiles.slice(0, available);
      if (toAdd.length === 0) return;

      const { prepared, anyCompressed, error } = await prepareImageUploads(toAdd, language);
      if (error) {
        setEditImagesError(error);
        return;
      }
      setEditImagesError(null);
      if (anyCompressed) {
        setToast({ message: uploadCopy.compressedNotice, type: 'success' });
        setTimeout(() => setToast(null), 3000);
      }
      setEditNewImages((prev) => [...prev, ...prepared]);
      setEditNewPreviews((prev) => [...prev, ...prepared.map((file) => URL.createObjectURL(file))]);
      setDidEditImages(true);
      if (editImagesInputRef.current) editImagesInputRef.current.value = '';
    } finally {
      isAddingImagesRef.current = false;
      setIsAddingImages(false);
    }
  };

  const removeEditExistingImage = (index: number) => {
    setEditExistingImages((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    setDidEditImages(true);
  };

  const removeEditNewImage = (index: number) => {
    const url = editNewPreviews[index];
    if (url) URL.revokeObjectURL(url);
    setEditNewImages((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    setEditNewPreviews((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    setDidEditImages(true);
  };

  const handleEditCategoryToggle = (slug: string) => {
    setEditForm((currentForm) => {
      if (currentForm.categorySlugs.includes(slug)) {
        return {
          ...currentForm,
          categorySlugs: currentForm.categorySlugs.filter((currentSlug) => currentSlug !== slug),
        };
      }

      if (currentForm.categorySlugs.length >= MAX_CATEGORIES) {
        return currentForm;
      }

      return {
        ...currentForm,
        categorySlugs: [...currentForm.categorySlugs, slug],
      };
    });
  };

  const buildUpdatePayload = () => {
    return {
      title: editForm.title,
      description: editForm.description,
      date: editForm.date,
      // Carried only while a recurring cadence is selected -- that is the
      // conversion payload (MWBI-955). Sending null on the one-off branch is
      // deliberate: it clears the stale span left on a row that the bug put
      // into the recurring-with-no-series shape, so picking "one-off" repairs
      // it. On the series-occurrence path updateOccurrences discards this.
      endDate: isRecurringEventType(editForm.eventType) ? editForm.endDate : null,
      timeStart: editForm.timeStart,
      timeEnd: editForm.timeEnd,
      location: editForm.location,
      cost: editForm.cost.trim(),
      bookingLink: editForm.bookingLink,
      openToPublic: editForm.openToPublic,
      eventType: editForm.eventType,
      categorySlugs: editForm.categorySlugs,
      // Only manage the image set when the admin actually changed it. Omitting
      // these leaves the existing `images`/`image` relations untouched, so a
      // text-only save can never clear a photo (MWBI-889 review).
      ...(didEditImages
        ? {
            existingImageIds: editExistingImages.map((img) => img.id),
            newImages: editNewImages,
          }
        : {}),
    };
  };

  const saveEdit = async (scope?: RecurringEventScope) => {
    // Defense-in-depth: never persist an edit that still contains inappropriate
    // language, regardless of which path (one-off vs recurring scope) reached
    // here (MWBI-870). The Save button is disabled and handleSaveEdit gates the
    // scope modal, but both saveEdit callers funnel through this guard too.
    // Same for a blank cost (MWBI-919) — the recurring scope modal's confirm
    // button calls saveEdit directly, so it needs the guard here as well as on
    // the control itself. A missing or backwards recurrence end date (MWBI-955)
    // blocks for the same reason: the conversion endpoint would reject it, and
    // a silent no-op is the bug being fixed.
    if (!event || isEditBlocked) return;

    const recurringContext = scope ? { scope } : undefined;

    setEditLoading(true);
    const result = await updateEvent(
      documentId,
      buildUpdatePayload(),
      recurringContext,
    );
    setEditLoading(false);

    if (!result.success) {
      setToast({ message: result.error || t.eventDetail.failedToUpdate, type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    editNewPreviews.forEach((url) => URL.revokeObjectURL(url));
    setEditExistingImages([]);
    setEditNewImages([]);
    setEditNewPreviews([]);
    setEditImagesError(null);
    setDidEditImages(false);
    isAddingImagesRef.current = false;
    setIsAddingImages(false);
    setIsEditMode(false);
    setShowRecurringEditScopeModal(false);
    setSelectedRecurringEditScope(null);
    setToast({ message: t.eventDetail.eventUpdated, type: 'success' });
    if (currentUser) {
      ActivityLogger.editEvent(currentUser, editForm.title, result.eventDocumentId ?? documentId);
    }

    await fetchEventDetail({
      preserveCurrentEventOnError: true,
      logErrors: false,
    });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSaveEdit = async () => {
    // Inappropriate language blocks the save outright. Gate here — before the
    // recurring scope modal can open — so a blocked edit never reaches the
    // scope prompt or the updateEvent flow (MWBI-870). The Save button is also
    // disabled on hasInappropriate; this guard covers a programmatic click.
    // A blank cost blocks the save the same way and for the same reason
    // (MWBI-919): the scope modal has no cost field, so letting it open with an
    // invalid cost would strand the user in a prompt that cannot succeed. An
    // invalid recurrence end date (MWBI-955) blocks on the same principle.
    if (!event || isEditBlocked) return;

    // Recurring (series) occurrences prompt for the edit scope so the user
    // can choose "this event" vs "this and future" before the mutation runs.
    if (shouldPromptForRecurringScope) {
      setSelectedRecurringEditScope(null);
      setShowRecurringEditScopeModal(true);
      return;
    }

    await saveEdit();
  };

  const handleRecurringEditScopeSubmit = async () => {
    if (!selectedRecurringEditScope) {
      setToast({ message: t.eventDetail.recurrenceScopeSelectionRequired, type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    await saveEdit(selectedRecurringEditScope);
  };

  const handleDeleteClick = () => {
    if (shouldPromptForRecurringScope) {
      setSelectedRecurringDeleteScope(null);
      setShowRecurringDeleteScopeModal(true);
      return;
    }

    setShowDeleteConfirm(true);
  };

  const handleRecurringDeleteScopeSubmit = () => {
    if (!selectedRecurringDeleteScope) {
      setToast({ message: t.eventDetail.recurrenceScopeSelectionRequired, type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setShowRecurringDeleteScopeModal(false);
    setShowDeleteConfirm(true);
  };

  const handleDeleteEvent = async () => {
    const recurringContext = isSeriesOccurrence && selectedRecurringDeleteScope
      ? { scope: selectedRecurringDeleteScope }
      : undefined;

    setEditLoading(true);
    const result = await deleteEvent(documentId, recurringContext);
    setEditLoading(false);
    if (result.success) {
      setToast({ message: t.eventDetail.eventDeleted, type: 'success' });
      if (currentUser && event) {
        ActivityLogger.deleteEvent(currentUser, event.title, result.eventDocumentId ?? documentId);
      }
      setTimeout(() => router.push('/events'), 1500);
    } else {
      setToast({ message: t.eventDetail.failedToDelete, type: 'error' });
      setTimeout(() => setToast(null), 3000);
    }
    setShowDeleteConfirm(false);
    setShowRecurringDeleteScopeModal(false);
    setSelectedRecurringDeleteScope(null);
  };

  if (loading) return <div className="p-3 md:p-6 text-center text-foreground-muted">{t.eventDetail.loadingEvent}</div>;
  if (!event) notFound();

  const eventImages = event.images && event.images.length > 0
    ? event.images
    : event.image?.url
      ? [{ id: event.image.id ?? event.id, url: event.image.url, alternativeText: event.image.alternativeText }]
      : [];
  const formatDisplayDate = (dateString: string, includeWeekday = false) => parseDateOnly(dateString).toLocaleDateString(
    language === 'es' ? 'es-ES' : 'en-US',
    includeWeekday
      ? { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }
      : { month: 'long', day: 'numeric', year: 'numeric' }
  );
  const formattedDate = formatDisplayDate(event.date, true);
  const recurrenceFrequencyLabel = event.eventType === 'recurring-daily'
    ? t.volunteers.frequencies.daily
    : event.eventType === 'recurring-weekly'
      ? t.volunteers.frequencies.weekly
      : event.eventType === 'recurring-bi-weekly'
        ? t.volunteers.frequencies.biWeekly
        : event.eventType === 'recurring-monthly'
          ? t.volunteers.frequencies.monthly
          : '';
  // An occurrence row's own endDate is null; derive the series end from the
  // latest fetched sibling row. Omit the "until X" text when no siblings loaded.
  const recurrenceEndsText = isRecurringEventType(event.eventType) && seriesEndDate
    ? t.eventDetail.recurrenceEnds
        .replace('{frequency}', recurrenceFrequencyLabel)
        .replace('{endDate}', formatDisplayDate(seriesEndDate))
    : null;
  const eventCategories = getEventCategories(event);
  const attendees = getAttendees();
  const socialTabs: { id: EventSocialTab; label: string }[] = [
    { id: 'forum', label: t.groupDetail.forum },
    { id: 'attendees', label: t.eventDetail.attendees },
  ];
  // "More Events": upcoming events other than the current one, soonest first.
  const moreEvents = allEvents
    .filter((relatedEvent) => {
      if (relatedEvent.documentId === documentId) return false;

      if (relatedEvent.openToPublic === false && relatedEvent.group?.id) {
        return currentUser ? isUserMemberOfGroup(currentUser.id, relatedEvent.group.id) : false;
      }

      return true;
    })
    // Keep only events that come after the current event's date.
    .filter((relatedEvent) => relatedEvent.date > event.date)
    // Show the next ones chronologically.
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const displayedMoreEvents = moreEvents.slice(0, 3);
  const mobileMoreEvent = displayedMoreEvents[0];
  const getEventPreviewUrl = (previewEvent: Event) =>
    getStrapiImageUrl(previewEvent.image?.url || previewEvent.images?.[0]?.url);

  return (
    <div className="max-w-6xl mx-auto p-3 md:p-6 space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {showPostModal && event && (
        <CreateNewsModal
          isOpen={showPostModal}
          onClose={() => setShowPostModal(false)}
          onResult={(success, message) => {
            setShowPostModal(false);
            setToast({ message, type: success ? 'success' : 'error' });
            setTimeout(() => setToast(null), 3000);
          }}
          preselectedGroupId={event.group?.id}
          initialContent={`${typeof window !== 'undefined' ? window.location.origin : ''}/events/${documentId}`}
          initialPreloadedFiles={postModalFiles}
        />
      )}

      {/* Header: back link + (for editors) the admin action bar. The row
          keeps a fixed min-height so the admin bar — which mounts only
          after the async permission check resolves — does not shift the
          page down when it appears. */}
      <div className="flex items-center justify-between min-h-[38px]">
        <Link href="/events" data-testid="event-detail-back-link" className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition">
          <ArrowLeft size={16} /> {t.eventDetail.backToEvents}
        </Link>
        {canEdit && !isEditMode && (
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                const imageUrl = event
                  ? getStrapiImageUrl(event.image?.url ?? event.images?.[0]?.url)
                  : null;
                if (imageUrl) {
                  try {
                    const res = await fetch(`/api/image-proxy?url=${encodeURIComponent(imageUrl)}`);
                    if (res.ok) {
                      const blob = await res.blob();
                      const file = new File([blob], 'event-image.jpg', { type: blob.type });
                      const preview = URL.createObjectURL(blob);
                      setPostModalFiles([{ file, preview }]);
                    } else {
                      setPostModalFiles([]);
                    }
                  } catch (error) {
                    console.error('src/app/events/[documentId]/page.tsx', error);
                    setPostModalFiles([]);
                  }
                } else {
                  setPostModalFiles([]);
                }
                setShowPostModal(true);
              }}
              data-testid="event-detail-post-event-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-black bg-white border border-border rounded-md hover:opacity-90 transition"
            >
              <Newspaper size={16} /> {t.eventDetail.postEvent}
            </button>
            <button
              onClick={handleStartEdit}
              data-testid="event-detail-edit-event-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 transition"
            >
              <Pencil size={16} /> {t.eventDetail.editEvent}
            </button>
          </div>
        )}
      </div>

      {/* Edit Mode Action Bar */}
      {isEditMode && (
        <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
          <button
            onClick={handleDeleteClick}
            disabled={editLoading}
            data-testid="event-detail-delete-event-btn"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition disabled:opacity-50"
          >
            <Trash2 size={16} />
            {t.eventDetail.deleteEvent}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancelEdit}
              disabled={editLoading}
              data-testid="event-detail-cancel-edit-btn"
              className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
            >
              {t.eventDetail.cancelEdit}
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={editLoading || isEditBlocked}
              aria-describedby={editBlockedDescribedBy}
              data-testid="event-detail-save-changes-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 transition disabled:opacity-50"
            >
              {editLoading ? t.eventDetail.updating : t.eventDetail.saveChanges}
            </button>
          </div>
        </div>
      )}

      {/* Content moderation (MWBI-870) — edit form only. Gated on `isEditMode`
          (only reachable via the canEdit-gated Edit button) so the banner/modals
          are present exactly when Save can be blocked — matching the Save button,
          which is gated on `isEditMode` alone. Crisis is informational and never
          blocks; inappropriate language shows an ack modal + banner and disables
          Save (see the Save button + handleSaveEdit/saveEdit gates). */}
      {isEditMode && (
        <>
          <CrisisModal
            open={showCrisisModal}
            onClose={dismissCrisisModal}
            testIdPrefix="event-detail-edit-crisis"
          />
          <InappropriateLanguageModal
            open={showInappropriateModal}
            onClose={dismissInappropriateModal}
            testIdPrefix="event-detail-edit-inappropriate"
          />
          {hasInappropriate && (
            <InappropriateLanguageBanner testIdPrefix="event-detail-edit-inappropriate" />
          )}
        </>
      )}

      <div className="flex flex-col lg:flex-row gap-8 bg-white border border-border rounded-xl p-6 max-lg:overflow-hidden">
        {/* Image - editable */}
        <div className="lg:w-[500px] flex-shrink-0 max-lg:-mx-6 max-lg:w-[calc(100%+3rem)]">
          {isEditMode ? (
            <div>
              {/* All existing photos plus any newly added ones, each removable, so
                  the admin can see and change every picture (MWBI-889). Mirrors
                  the Groups edit gallery. */}
              <p className="text-sm font-medium text-foreground mb-2">
                {t.modals.event.eventPhoto} ({editExistingImages.length + editNewImages.length}/{MAX_EDIT_IMAGES})
              </p>
              {(editExistingImages.length > 0 || editNewImages.length > 0) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                  {editExistingImages.map((img, index) => (
                    <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-background-secondary">
                      <Image
                        src={getStrapiImageUrl(img.url) ?? ''}
                        alt={img.alternativeText || `${event.title} photo ${index + 1}`}
                        fill
                        className="object-contain"
                        unoptimized
                      />
                      <button
                        type="button"
                        onClick={() => removeEditExistingImage(index)}
                        data-testid={`event-detail-remove-existing-image-btn-${img.id}`}
                        aria-label={`${t.eventDetail.removePhoto} ${index + 1}`}
                        className="absolute top-1 right-1 bg-error text-white rounded-full p-0.5"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {editNewPreviews.map((preview, index) => (
                    <div key={preview} className="relative aspect-square rounded-lg overflow-hidden border border-primary bg-background-secondary">
                      <Image src={preview} alt={`New photo ${index + 1}`} fill className="object-contain" unoptimized />
                      <button
                        type="button"
                        onClick={() => removeEditNewImage(index)}
                        data-testid={`event-detail-remove-new-image-btn-${index}`}
                        aria-label={`${t.eventDetail.removePhoto} ${editExistingImages.length + index + 1}`}
                        className="absolute top-1 right-1 bg-error text-white rounded-full p-0.5"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!isAddingImages && editExistingImages.length + editNewImages.length < MAX_EDIT_IMAGES) {
                    editImagesInputRef.current?.click();
                  }
                }}
                disabled={isAddingImages || editExistingImages.length + editNewImages.length >= MAX_EDIT_IMAGES}
                data-testid="event-detail-edit-add-images-btn"
                className="flex items-center gap-2 text-sm font-medium text-foreground border border-border px-3 py-1.5 rounded-md hover:bg-sidebar-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ImagePlus size={16} /> {uploadCopy.uploadPrompt}
              </button>
              <p className="text-xs text-caption mt-1">{uploadCopy.fileTypes}</p>
              {editImagesError && <p className="text-xs text-error mt-1" role="alert">{editImagesError}</p>}
              <input
                ref={editImagesInputRef}
                type="file"
                accept="image/png,image/jpeg"
                multiple
                data-testid="event-detail-edit-image-input"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    void handleEditImagesAdd(e.target.files);
                  }
                }}
              />
            </div>
          ) : eventImages.length > 0 ? (
            <ImageCarousel images={eventImages} containerClassName="max-lg:rounded-none" />
          ) : (
            <div className="relative h-64 lg:h-80 rounded-lg max-lg:rounded-none overflow-hidden bg-background-secondary">
              <Image src="/NoImage.jpg" alt={event.title} fill className="object-cover" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Category - editable */}
            {isEditMode ? (
              <div className="w-full rounded-lg border border-border bg-background-secondary p-3">
                <p className="text-xs font-medium text-foreground mb-2">
                  {t.modals.event.categoriesOptional} ({t.modals.event.selectUpToFour})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {sortByLabel(
                    categories.filter((category) => !category.archived),
                    (category) => getEventCategoryLabelI18n(t, category.slug, category.name),
                    language,
                  )
                    .map((category) => {
                      const isSelected = editForm.categorySlugs.includes(category.slug);
                      const isDisabled = !isSelected && editForm.categorySlugs.length >= MAX_CATEGORIES;

                      return (
                        <label
                          key={category.slug}
                          className="flex items-center gap-2 cursor-pointer rounded p-1 hover:bg-background"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleEditCategoryToggle(category.slug)}
                            disabled={isDisabled}
                            data-testid={`event-detail-edit-category-${category.slug}`}
                            className="w-4 h-4 rounded border-border"
                          />
                          <span className="text-sm text-foreground">{getEventCategoryLabelI18n(t, category.slug, category.name)}</span>
                        </label>
                      );
                    })}
                </div>
                <p className="mt-2 text-xs text-caption">
                  {t.modals.event.categoriesSelected.replace('{count}', String(editForm.categorySlugs.length))}
                </p>
              </div>
            ) : (
              sortByLabel(eventCategories, (category) => getEventCategoryLabelI18n(t, category.slug, category.name), language).map((category) => (
                <span
                  key={category.documentId}
                  className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-background-secondary text-foreground-muted border border-border"
                >
                  {getEventCategoryLabelI18n(t, category.slug, category.name)}
                </span>
              ))
            )}
          </div>

          {/* Title - editable */}
          {isEditMode ? (
            <input
              type="text"
              value={editForm.title}
              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
              data-testid="event-detail-edit-title-input"
              className="w-full text-3xl font-bold text-foreground bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          ) : (
            <h1 className="text-3xl font-bold text-foreground">{event.title}</h1>
          )}

          <div className="space-y-2 text-sm text-foreground-muted">
            {/* Date & Event Type - editable */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-primary" />
                {/* A series occurrence's date and cadence are fixed: edits
                    route through update-occurrences, which never persists
                    per-occurrence date or eventType changes. Render them
                    read-only here so the form does not promise a no-op
                    (MWBI-684). */}
                {isEditMode && !isSeriesOccurrence ? (
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                    data-testid="event-detail-edit-date-input"
                    className="px-2 py-1 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                ) : (
                  <span>{formattedDate}</span>
                )}
              </div>
              {/* Event Type - editable for standalone events only */}
              {isEditMode && !isSeriesOccurrence && (
                <select
                  value={editForm.eventType}
                  onChange={(e) => setEditForm(prev => ({ ...prev, eventType: e.target.value as EventType }))}
                  data-testid="event-detail-edit-event-type-select"
                  className="px-2 py-1 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="one-off">{t.modals.event.oneOff}</option>
                  <option value="recurring-daily">{t.modals.event.recurringDaily}</option>
                  <option value="recurring-weekly">{t.modals.event.recurringWeekly}</option>
                  <option value="recurring-bi-weekly">{t.modals.event.recurringBiWeekly}</option>
                  <option value="recurring-monthly">{t.modals.event.recurringMonthly}</option>
                </select>
              )}
              {/* End date - required once a recurring cadence is chosen, because
                  that is what converts this standalone event into a real series
                  (MWBI-955). Mirrors CreateEventModal's end-date field and the
                  cost field's error affordances below: aria-label (the row has
                  no visible label), aria-invalid, aria-describedby pointing at a
                  role="alert" reason, and a border colour that is never the only
                  signal. */}
              {isEditMode && !isSeriesOccurrence && isRecurringEventType(editForm.eventType) && (
                <div>
                  <input
                    type="date"
                    value={editForm.endDate}
                    onChange={(e) => setEditForm(prev => ({ ...prev, endDate: e.target.value }))}
                    min={editForm.date || undefined}
                    aria-label={t.modals.event.endDate}
                    aria-invalid={isEditEndDateInvalid || undefined}
                    aria-describedby={isEditEndDateInvalid ? EDIT_END_DATE_ERROR_ID : undefined}
                    data-testid="event-detail-edit-end-date-input"
                    className={`px-2 py-1 border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary ${isEditEndDateInvalid ? ERROR_BORDER_CLASS : 'border-border'}`}
                  />
                  {isEditEndDateInvalid && (
                    <p
                      id={EDIT_END_DATE_ERROR_ID}
                      role="alert"
                      data-testid="event-detail-edit-end-date-error"
                      className="text-xs text-red-500 font-medium mt-1"
                    >
                      {isEditEndDateMissing
                        ? t.modals.event.recurrenceEndRequired
                        : t.modals.event.recurrenceEndAfterStart}
                    </p>
                  )}
                </div>
              )}
            </div>

            {!isEditMode && recurrenceEndsText && (
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-primary" />
                <span>{recurrenceEndsText}</span>
              </div>
            )}

            {/* Time - editable */}
            <div className="flex items-center gap-2">
              <Clock size={16} />
              {isEditMode ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <TimePicker
                    value={editForm.timeStart}
                    onChange={(val) => setEditForm(prev => ({ ...prev, timeStart: val }))}
                    compact
                    testIdPrefix="event-detail-edit-start"
                  />
                  <span>-</span>
                  <TimePicker
                    value={editForm.timeEnd}
                    onChange={(val) => setEditForm(prev => ({ ...prev, timeEnd: val }))}
                    testIdPrefix="event-detail-edit-end"
                    compact
                  />
                </div>
              ) : (
                <span>{formatTime12H(event.timeStart)} - {formatTime12H(event.timeEnd)}</span>
              )}
            </div>

            {/* Location - editable */}
            <div className="flex items-center gap-2">
              <MapPin size={16} />
              {isEditMode ? (
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                  data-testid="event-detail-edit-location-input"
                  className="flex-1 px-2 py-1 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              ) : (
                <span>{event.location}</span>
              )}
            </div>

            {event.group && (
              <Link href={`/groups/${event.group.documentId}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                <Building2 size={16} />
                <span>{event.group.name}</span>
              </Link>
            )}
            <button
              onClick={() => attendeeCount > 0 && setShowAttendeesModal(true)}
              data-testid="event-detail-attendee-avatars-btn"
              className={`${attendeeCount > 0 ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
            >
              <AttendeeAvatars
                attendees={event.attendees || []}
                users={users}
                maxVisible={7}
                size="md"
              />
            </button>

            {/* Cost - editable. Free text, shown exactly as typed (MWBI-919);
                same control and same limit as the Create Event modal. */}
            <div className="flex items-start gap-2">
              <DollarSign size={16} className="mt-1 shrink-0" />
              {isEditMode ? (
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={editForm.cost}
                    onChange={(e) => setEditForm(prev => ({ ...prev, cost: e.target.value }))}
                    placeholder={t.modals.event.costPlaceholder}
                    maxLength={FORM_LIMITS.eventCost.max}
                    aria-label={t.modals.event.cost}
                    aria-describedby={isEditCostMissing ? EDIT_COST_REQUIRED_ID : undefined}
                    data-testid="event-detail-edit-cost-input"
                    className={`w-full px-2 py-1 border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary ${isEditCostMissing ? ERROR_BORDER_CLASS : 'border-border'}`}
                  />
                  {isEditCostMissing && (
                    <p
                      id={EDIT_COST_REQUIRED_ID}
                      role="alert"
                      data-testid="event-detail-edit-cost-required"
                      className="text-xs text-red-500 font-medium mt-1"
                    >
                      {t.modals.event.costRequired}
                    </p>
                  )}
                </div>
              ) : (
                <span data-testid="event-detail-cost">{getEventCostLabel(event, t)}</span>
              )}
            </div>

            {/* Booking Link - editable */}
            {isEditMode && (
              <div className="flex items-center gap-2">
                <ExternalLink size={16} />
                <input
                  type="url"
                  value={editForm.bookingLink}
                  onChange={(e) => setEditForm(prev => ({ ...prev, bookingLink: e.target.value }))}
                  placeholder={t.modals.event.bookingLinkPlaceholder}
                  data-testid="event-detail-edit-booking-link-input"
                  className="flex-1 px-2 py-1 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}

            {/* Event Access - edit only */}
            {isEditMode && (
              <div className="flex items-start gap-3 pt-1">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{t.modals.event.eventAccess}</p>
                  <p className="text-xs text-foreground-muted">
                    {editForm.openToPublic ? t.modals.event.anyoneCanRsvp : t.modals.event.onlyMembersRsvp}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={editForm.openToPublic}
                  onClick={() => setEditForm(prev => ({ ...prev, openToPublic: !prev.openToPublic }))}
                  data-testid="event-detail-edit-open-to-public-toggle"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    editForm.openToPublic ? 'bg-primary' : 'bg-border'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editForm.openToPublic ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )}

          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <>
              <button
                onClick={handleRSVP}
                disabled={rsvpLoading || isPastEvent}
                data-testid={isAttending ? 'event-detail-cancel-rsvp-btn' : 'event-detail-rsvp-btn'}
                title={isPastEvent ? t.eventDetail.eventEnded : undefined}
                className="flex items-center gap-2 px-4 py-2 bg-action-green text-black rounded-md text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
              >
                {rsvpLoading
                  ? t.eventDetail.updating
                  : isPastEvent
                    ? t.eventDetail.eventEnded
                    : isAttending
                      ? t.eventDetail.cancelRsvp
                      : t.eventDetail.letFriendsKnow}
              </button>

              {normalizedBookingLink && (
                <a
                  href={normalizedBookingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="event-detail-register-link"
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:opacity-90 transition"
                >
                  {t.eventDetail.register} <ExternalLink size={16} />
                </a>
              )}
            </>
            <span data-testid="event-detail-report-btn">
              <ReportButton
                contentType="event"
                contentId={event.documentId}
                contentTitle={event.title}
                className="inline-flex items-center justify-center w-10 h-10 border border-border rounded-md text-foreground-muted hover:bg-sidebar-hover transition"
              />
            </span>
            <div className="relative">
              <button
                onClick={() => setShowCalendarOptions(!showCalendarOptions)}
                data-testid="event-detail-add-to-calendar-btn"
                className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm text-foreground hover:bg-sidebar-hover transition"
              >
                <Calendar size={16} /> {t.eventDetail.addToCalendar}
              </button>
              {showCalendarOptions && (
                <div className="absolute top-full mt-2 left-0 bg-background border border-border rounded-lg shadow-lg z-10 overflow-hidden min-w-[160px]">
                  <button
                    onClick={() => handleAddToCalendar('google')}
                    data-testid="event-detail-google-calendar-option"
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-sidebar-hover transition"
                  >
                    Google Calendar
                  </button>
                  <button
                    onClick={() => handleAddToCalendar('outlook')}
                    data-testid="event-detail-outlook-calendar-option"
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-sidebar-hover transition"
                  >
                    Outlook Calendar
                  </button>
                </div>
              )}
            </div>
            <button onClick={handleShare} data-testid="event-detail-share-btn" className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm text-foreground hover:bg-sidebar-hover transition">
              <Share2 size={16} /> {t.common.share}
            </button>
          </div>

          {/* Previous / next occurrence navigation - series rows only */}
          {(previousOccurrence || nextOccurrence) && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {previousOccurrence && (
                <Link
                  href={`/events/${previousOccurrence.documentId}`}
                  data-testid="event-detail-previous-occurrence-link"
                  className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-primary transition"
                >
                  <ArrowLeft size={14} data-testid="event-detail-previous-occurrence-icon" /> {formatDisplayDate(previousOccurrence.date)}
                </Link>
              )}
              {previousOccurrence && nextOccurrence && (
                <span className="text-foreground-muted">|</span>
              )}
              {nextOccurrence && (
                <Link
                  href={`/events/${nextOccurrence.documentId}`}
                  data-testid="event-detail-next-occurrence-link"
                  className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-primary transition"
                >
                  {formatDisplayDate(nextOccurrence.date)} <ArrowRight size={14} data-testid="event-detail-next-occurrence-icon" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Description - editable */}
      {(event.description || isEditMode) && (
        <div className="space-y-2 bg-white border border-border rounded-xl p-6">
          <h2 className="text-xl font-semibold text-foreground">{t.eventDetail.aboutThisEvent}</h2>
          {isEditMode ? (
            <textarea
              value={editForm.description}
              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              data-testid="event-detail-edit-description-textarea"
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              placeholder={t.modals.event.describeEvent}
            />
          ) : (
            <p data-testid="event-detail-about-text" className="text-foreground text-base leading-7 whitespace-pre-line break-words">{linkifyText(event.description || '')}</p>
          )}
        </div>
      )}

      {event.agenda && event.agenda.length > 0 && (
        <div className="space-y-3 bg-white border border-border rounded-xl p-6">
          <h2 className="text-xl font-semibold text-foreground">{t.eventDetail.eventAgenda}</h2>
          <div className="space-y-2">
            {event.agenda.map((item, index) => (
              <div key={index} className="flex gap-6 py-2 border-b border-border last:border-0">
                <span className="text-foreground-muted w-24 shrink-0">{item.time}</span>
                <span className="text-foreground">{item.activity}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {event.accessibilityNotes && (
        <div className="space-y-2 p-6 bg-white rounded-xl border border-border">
          <h3 className="flex items-center gap-2 font-semibold text-foreground">
            <Accessibility size={18} /> {t.eventDetail.accessibilityInfo}
          </h3>
          <p className="text-foreground-muted text-sm">{event.accessibilityNotes}</p>
        </div>
      )}

      {canViewEventTabs && (
        <div className="rounded-xl overflow-hidden">
          <div className="flex gap-2 px-4 md:px-6 py-3 border-b border-border overflow-x-auto">
            {socialTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSocialTab(tab.id)}
                data-testid={tab.id === 'forum' ? 'event-detail-forum-tab' : 'event-detail-attendees-tab'}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition whitespace-nowrap border ${activeSocialTab === tab.id ? 'bg-tab-active-bg border-filter-bg text-black' : 'bg-white border-border text-foreground-muted hover:border-primary hover:text-foreground'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeSocialTab === 'forum' ? (
              <EventForumTab
                eventDocumentId={event.documentId}
                eventTitle={event.title}
                canParticipate={canViewEventTabs}
              />
            ) : (
              <EventAttendeesTab
                attendees={attendees}
                attendeeCount={attendeeCount}
                canSendBulkEmail={canEdit}
                canDirectMessage={!!currentUser && canUseMessaging(currentUser)}
                currentUserId={currentUser?.id}
                eventId={event.id}
              />
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-foreground">{t.eventDetail.location}</h2>
        <p className="text-foreground-muted text-sm flex items-center gap-2"><MapPin size={16} /> {event.location}</p>
        <div className="h-64 rounded-lg overflow-hidden border border-border">
          <iframe
            src={`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>

      {displayedMoreEvents.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-foreground">{t.eventDetail.moreEvents}</h2>

          {/* Mobile: 1 column and only 1 card */}
          <div className="md:hidden">
            {(() => {
              const relatedEvent = mobileMoreEvent;
              if (!relatedEvent) return null;
              const relatedFormattedDate = parseDateOnly(relatedEvent.date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              });

              return (
                <Link
                  href={`/events/${relatedEvent.documentId}`}
                  className="block bg-white border border-border rounded-lg overflow-hidden hover:shadow-md transition"
                >
                  <div className="relative h-48 bg-background-secondary">
                    {getEventPreviewUrl(relatedEvent) ? (
                      <Image
                        src={getEventPreviewUrl(relatedEvent) || ''}
                        alt={relatedEvent.title}
                        fill
                        className="object-cover"
                        unoptimized
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Calendar size={48} className="text-foreground-muted/30" />
                      </div>
                    )}
                  </div>

                  <div className="p-4 space-y-3">
                    {getEventCategories(relatedEvent).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {sortByLabel(getEventCategories(relatedEvent), (category) => getEventCategoryLabelI18n(t, category.slug, category.name), language).map((category) => (
                          <span
                            key={category.documentId}
                            className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-background-secondary text-foreground-muted border border-border"
                          >
                            {getEventCategoryLabelI18n(t, category.slug, category.name)}
                          </span>
                        ))}
                      </div>
                    )}

                    <h3 className="font-semibold text-foreground line-clamp-2">{relatedEvent.title}</h3>

                    <div className="space-y-2 text-sm text-foreground-muted">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="shrink-0 text-primary" />
                        <span>{relatedFormattedDate}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="shrink-0" />
                        <span data-testid="related-event-time-mobile">{formatTime12H(relatedEvent.timeStart)} - {formatTime12H(relatedEvent.timeEnd)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="shrink-0" />
                        <span className="line-clamp-1">{relatedEvent.location}</span>
                      </div>
                      <AttendeeAvatars
                        attendees={relatedEvent.attendees || []}
                        users={users}
                        maxVisible={3}
                        size="sm"
                      />
                    </div>

                    <p className="text-sm font-medium text-foreground">
                      {getEventCostLabel(relatedEvent, t)}
                    </p>
                  </div>
                </Link>
              );
            })()}
          </div>

          {/* Desktop: 3-column grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6">
            {displayedMoreEvents.map((relatedEvent) => {
              const relatedFormattedDate = parseDateOnly(relatedEvent.date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              });

              return (
              <Link
                key={relatedEvent.documentId}
                href={`/events/${relatedEvent.documentId}`}
                className="block bg-white border border-border rounded-lg overflow-hidden hover:shadow-md transition"
              >
                <div className="relative h-48 bg-background-secondary">
                  {getEventPreviewUrl(relatedEvent) ? (
                    <Image
                      src={getEventPreviewUrl(relatedEvent) || ''}
                      alt={relatedEvent.title}
                      fill
                      className="object-cover"
                      unoptimized
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Calendar size={48} className="text-foreground-muted/30" />
                    </div>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  {getEventCategories(relatedEvent).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {sortByLabel(getEventCategories(relatedEvent), (category) => getEventCategoryLabelI18n(t, category.slug, category.name), language).map((category) => (
                        <span
                          key={category.documentId}
                          className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-background-secondary text-foreground-muted border border-border"
                        >
                          {getEventCategoryLabelI18n(t, category.slug, category.name)}
                        </span>
                      ))}
                    </div>
                  )}

                  <h3 className="font-semibold text-foreground line-clamp-2">{relatedEvent.title}</h3>

                  <div className="space-y-2 text-sm text-foreground-muted">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="shrink-0 text-primary" />
                      <span>{relatedFormattedDate}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="shrink-0" />
                      <span data-testid="related-event-time-desktop">{formatTime12H(relatedEvent.timeStart)} - {formatTime12H(relatedEvent.timeEnd)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="shrink-0" />
                      <span className="line-clamp-1">{relatedEvent.location}</span>
                    </div>
                    <AttendeeAvatars
                      attendees={relatedEvent.attendees || []}
                      users={users}
                      maxVisible={3}
                      size="sm"
                    />
                  </div>

                  <p className="text-sm font-medium text-foreground">
                    {getEventCostLabel(relatedEvent, t)}
                  </p>
                </div>
              </Link>
              );
            })}
          </div>
        </div>
      )}

      {showAttendeesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAttendeesModal(false)} />
          <div className="relative bg-background rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">{t.eventDetail.attendees} ({attendeeCount})</h2>
              <button onClick={() => setShowAttendeesModal(false)} className="p-1 hover:bg-sidebar-hover rounded transition">
                <X size={20} className="text-foreground-muted" />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh]">
              {attendees.length === 0 ? (
                <p className="text-center text-foreground-muted py-8">{t.eventDetail.noAttendeesYet}</p>
              ) : (
                attendees.map((user) => (
                  <Link
                    key={user.id}
                    href={getUserProfileHref(user)}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-sidebar-hover transition"
                    onClick={() => setShowAttendeesModal(false)}
                  >
                    {user.avatarUrl ? (
                      <Image src={user.avatarUrl} alt={user.displayName} width={40} height={40} className="w-10 h-10 rounded-full object-contain" unoptimized />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ backgroundColor: user.avatarColor }}
                      >
                        {user.initials}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{user.displayName}</p>
                      {user.location && <p className="text-xs text-foreground-muted">{user.location}</p>}
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={showRecurringDeleteScopeModal}
        onClose={() => {
          if (editLoading) return;
          setShowRecurringDeleteScopeModal(false);
          setSelectedRecurringDeleteScope(null);
        }}
        title={t.eventDetail.recurrenceDeleteScopeTitle}
        subtitle={t.eventDetail.recurrenceDeleteScopeDescription}
        footer={(
          <>
            <button
              onClick={() => {
                setShowRecurringDeleteScopeModal(false);
                setSelectedRecurringDeleteScope(null);
              }}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
            >
              {t.eventDetail.cancelEdit}
            </button>
            <button
              onClick={handleRecurringDeleteScopeSubmit}
              disabled={!selectedRecurringDeleteScope || editLoading}
              data-testid="event-detail-recurring-scope-submit-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 transition disabled:opacity-50"
            >
              {t.common.submit}
            </button>
          </>
        )}
      >
        <div className="space-y-3" role="radiogroup" aria-label={t.eventDetail.recurrenceDeleteScopeTitle}>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition hover:bg-sidebar-hover">
            <input
              type="radio"
              name="recurring-delete-scope"
              value="this-event"
              checked={selectedRecurringDeleteScope === 'this-event'}
              onChange={() => setSelectedRecurringDeleteScope('this-event')}
              data-testid="event-detail-recurring-delete-scope-this"
              className="mt-1"
            />
            <div>
              <p className="font-medium text-foreground">{t.eventDetail.recurrenceScopeThisEvent}</p>
              <p className="text-sm text-foreground-muted">{t.eventDetail.recurrenceScopeThisEventDescription}</p>
            </div>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition hover:bg-sidebar-hover">
            <input
              type="radio"
              name="recurring-delete-scope"
              value="this-and-future"
              checked={selectedRecurringDeleteScope === 'this-and-future'}
              onChange={() => setSelectedRecurringDeleteScope('this-and-future')}
              data-testid="event-detail-recurring-delete-scope-future"
              className="mt-1"
            />
            <div>
              <p className="font-medium text-foreground">{t.eventDetail.recurrenceScopeThisAndFuture}</p>
              <p className="text-sm text-foreground-muted">
                {t.eventDetail.recurrenceScopeThisAndFutureDescription}
              </p>
            </div>
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={showRecurringEditScopeModal}
        onClose={() => {
          if (editLoading) return;
          setShowRecurringEditScopeModal(false);
          setSelectedRecurringEditScope(null);
        }}
        title={t.eventDetail.recurrenceEditScopeTitle}
        subtitle={t.eventDetail.recurrenceEditScopeDescription}
        footer={(
          <>
            <button
              onClick={() => {
                setShowRecurringEditScopeModal(false);
                setSelectedRecurringEditScope(null);
              }}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
            >
              {t.eventDetail.cancelEdit}
            </button>
            {/* Second entry point to the same update mutation as the Save
                button, so it carries the same guards (MWBI-919). */}
            <button
              onClick={handleRecurringEditScopeSubmit}
              disabled={!selectedRecurringEditScope || editLoading || isEditBlocked}
              data-testid="event-detail-recurring-scope-submit-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 transition disabled:opacity-50"
            >
              {t.common.submit}
            </button>
          </>
        )}
      >
        <div className="space-y-3" role="radiogroup" aria-label={t.eventDetail.recurrenceEditScopeTitle}>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition hover:bg-sidebar-hover">
            <input
              type="radio"
              name="recurring-edit-scope"
              value="this-event"
              checked={selectedRecurringEditScope === 'this-event'}
              onChange={() => setSelectedRecurringEditScope('this-event')}
              data-testid="event-detail-recurring-edit-scope-this"
              className="mt-1"
            />
            <div>
              <p className="font-medium text-foreground">{t.eventDetail.recurrenceScopeThisEvent}</p>
              <p className="text-sm text-foreground-muted">{t.eventDetail.recurrenceScopeThisEventDescription}</p>
            </div>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition hover:bg-sidebar-hover">
            <input
              type="radio"
              name="recurring-edit-scope"
              value="this-and-future"
              checked={selectedRecurringEditScope === 'this-and-future'}
              onChange={() => setSelectedRecurringEditScope('this-and-future')}
              data-testid="event-detail-recurring-edit-scope-future"
              className="mt-1"
            />
            <div>
              <p className="font-medium text-foreground">{t.eventDetail.recurrenceScopeThisAndFuture}</p>
              <p className="text-sm text-foreground-muted">
                {t.eventDetail.recurrenceScopeThisAndFutureDescription}
              </p>
            </div>
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          if (editLoading) return;
          setShowDeleteConfirm(false);
        }}
        title={t.eventDetail.deleteConfirmTitle}
        subtitle={t.eventDetail.deleteConfirmMessage}
        footer={(
          <>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
            >
              {t.eventDetail.cancelEdit}
            </button>
            <button
              onClick={handleDeleteEvent}
              disabled={editLoading}
              data-testid="event-detail-confirm-delete-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition disabled:opacity-50"
            >
              {editLoading ? t.eventDetail.updating : t.eventDetail.confirmDelete}
            </button>
          </>
        )}
      >
        <div />
      </Modal>
    </div>
  );
}
